import React, { useEffect, useState, useCallback } from 'react';
import subscriptionService from '../../services/subscriptionService';
import { useI18n } from '../../contexts/I18nContext';
import toast from 'react-hot-toast';
import './MySubscriptions.scss';

const MySubscriptions = () => {
  const { t } = useI18n();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingSub, setPendingSub] = useState(null);

  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await subscriptionService.listSubscriptions();
      setSubscriptions(data || []);
    } catch (err) {
      setError(err.response?.data?.message || t('personalCenter.mySubscriptions.cancelError'));
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '-';
    }
  };

  const statusText = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return t('personalCenter.mySubscriptions.status.active');
    if (s === 'cancelled') return t('personalCenter.mySubscriptions.status.cancelled');
    if (s === 'suspended') return t('personalCenter.mySubscriptions.status.suspended');
    if (s === 'expired') return t('personalCenter.mySubscriptions.status.expired');
    return status || '-';
  };

  const statusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return 'completed';
    if (s === 'cancelled' || s === 'expired') return 'cancelled';
    return 'pending';
  };

  const handleCancel = (subscription) => {
    if (!subscription?.id) return;
    setPendingSub(subscription);
    setIsConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (cancellingId) return;
    setIsConfirmOpen(false);
    setPendingSub(null);
  };

  const confirmCancel = async () => {
    if (!pendingSub?.id) return;
    try {
      setCancellingId(pendingSub.id);
      await subscriptionService.cancelSubscription(pendingSub.id, false);
      toast.success(t('personalCenter.mySubscriptions.cancelSuccess'));
      await loadSubscriptions();
    } catch (err) {
      const msg = err.response?.data?.message || t('personalCenter.mySubscriptions.cancelError');
      toast.error(msg);
    } finally {
      setCancellingId(null);
      setIsConfirmOpen(false);
      setPendingSub(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('personalCenter.mySubscriptions.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadSubscriptions} className="retry-button btn">
          {t('personalCenter.mySubscriptions.retry')}
        </button>
      </div>
    );
  }

  if (!subscriptions || subscriptions.length === 0) {
    return (
      <div className="empty-state">
        <h3>{t('personalCenter.mySubscriptions.noSubscriptions')}</h3>
        <p>{t('personalCenter.mySubscriptions.noSubscriptionsDesc')}</p>
        <a href="/pricing" className="btn btn-primary" style={{ marginTop: 12 }}>
          {t('personalCenter.mySubscriptions.browsePlans')}
        </a>
      </div>
    );
  }

  return (
    <div className="my-subscriptions">
      <div className="page-header">
        <h1>{t('personalCenter.mySubscriptions.title')}</h1>
        <p>{t('personalCenter.mySubscriptions.description')}</p>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3>{t('personalCenter.mySubscriptions.title')}</h3>
        </div>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('orders.status')}</th>
                <th>{t('personalCenter.mySubscriptions.price')}</th>
                <th>{t('personalCenter.mySubscriptions.startDate')}</th>
                <th>{t('personalCenter.mySubscriptions.endDate')}</th>
                <th>{t('personalCenter.mySubscriptions.nextBilling')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub, idx) => {
                const id = sub.id || sub.subscriptionId || idx;
                const price = sub.price || sub.amount || sub.unitAmount;
                const currency = sub.currency || 'USD';
                const gateway = (sub.gateway || sub.provider || '').toLowerCase();
                const manageUrl = sub.manageUrl || '';
                const canCancel = ((sub.status || '').toLowerCase() === 'active');
                return (
                  <tr key={id}>
                    <td>{sub.publicId || id}</td>
                    <td>
                      <span className={`status ${statusClass(sub.status)}`}>{statusText(sub.status)}</span>
                    </td>
                    <td>{price ? `${price} ${currency}` : '-'}</td>
                    <td>{formatDate(sub.currentPeriodStart)}</td>
                    <td>{formatDate(sub.currentPeriodEnd)}</td>
                    <td>{formatDate(sub.nextBillingTime || sub.nextBilling)}</td>
                    <td style={{ display: 'flex', gap: 8 }}>
                      {canCancel && (
                        <button
                          className={`btn btn-text-danger ${cancellingId === (sub.id || sub.subscriptionId) ? 'loading' : ''}`}
                          onClick={() => handleCancel(sub)}
                          disabled={cancellingId === (sub.id || sub.subscriptionId)}
                        >
                          {t('personalCenter.mySubscriptions.cancelSubscription')}
                        </button>
                      )}
                      {manageUrl && (
                        <a href={manageUrl} target="_blank" rel="noreferrer" className="btn btn-outline">
                          {gateway === 'paypal' ? t('personalCenter.mySubscriptions.manageOnPayPal') : t('personalCenter.mySubscriptions.manageOnStripe')}
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {isConfirmOpen && (
        <div className="cancel-modal-overlay" onClick={closeConfirm}>
          <div className="cancel-modal-content" onClick={(e) => e.stopPropagation()}>
            {cancellingId === (pendingSub?.id || pendingSub?.subscriptionId) && (
              <div className="cancel-loading-overlay">
                <div className="cancel-loading-content">
                  <div className="loading-spinner"></div>
                  <p className="loading-text">{t('common.loading')}</p>
                </div>
              </div>
            )}
            <div className="cancel-modal-header">
              <h3 className="cancel-modal-title">{t('personalCenter.mySubscriptions.cancelSubscription')}</h3>
              <button className="cancel-modal-close" onClick={closeConfirm} disabled={!!cancellingId}>✕</button>
            </div>
            <div className="cancel-modal-body">
              <p className="cancel-modal-message">{t('personalCenter.mySubscriptions.confirmCancel')}</p>
            </div>
            <div className="cancel-modal-footer">
              <button
                className={`btn cancel-confirm-btn ${cancellingId ? 'loading' : ''}`}
                onClick={confirmCancel}
                disabled={!!cancellingId}
              >
                {t('refund.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubscriptions;
